import { useCallback, useEffect, useRef, useState } from 'react'
import Styles from './Screenshot.module.scss'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { useAsyncScreenshot } from 'hooks/queries/useAsyncScreenshot'

const SCREENSHOT_TIMEOUT = 60000

interface Props {
  url?: string
  handleBack: () => void
  cancelOnError?: boolean
}

export const Screenshot = ({ url, handleBack, cancelOnError = true }: Props) => {
  const onError = useCallback(() => {
    if (cancelOnError) handleBack()
  }, [cancelOnError, handleBack])

  const { screenshotUrl, createScreenshot } = useAsyncScreenshot({ onError })

  const [persistedUrl, setPersistedUrl] = useState<string | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasStartedRef = useRef(false)

  useEffect(() => {
    if (screenshotUrl && !persistedUrl) {
      setPersistedUrl(screenshotUrl)
    }
  }, [screenshotUrl, persistedUrl])

  useEffect(() => {
    if (hasStartedRef.current || !url) {
      return
    }

    hasStartedRef.current = true

    timeoutRef.current = setTimeout(() => {
      if (cancelOnError) handleBack()
    }, SCREENSHOT_TIMEOUT)

    createScreenshot(url)
      .catch(() => {
        // Errors are surfaced through onError; swallow to avoid unhandled rejections in the UI layer.
      })
      .finally(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      })

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [createScreenshot, url, handleBack, cancelOnError])

  const displayUrl = persistedUrl || screenshotUrl

  return (
    <div className={Styles.imageContainer}>
      {displayUrl ? (
        <img src={displayUrl} alt="Initial Screenshot" />
      ) : (
        <LoadingShimmerer>
          <div className={Styles.screenshotPlaceholder} />
        </LoadingShimmerer>
      )}
    </div>
  )
}
